import { useState } from "react";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { Button } from "@fawkes/ui";
import { Plus, RotateCcw, Trash2 } from "lucide-react";

import { foutBody } from "./types";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

/**
 * De rule builder van de reserveringswizard (ontwerp 12.1): ALS/EN/OF/DAN, geen vrije code. Een regel maakt een eigen
 * vraag zichtbaar wanneer zijn voorwaarden kloppen; een vraag met een regel is verborgen tot een regel klopt. De regels
 * staan in de portaalinstellingen (reserveringen.regels) en gaan mee in de momentopname van iedere aanvraag.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;

export interface Voorwaarde { veld: string; operator: "IS" | "IS_NIET"; waarde: string }
export interface Regel { id: string; verbinding: "EN" | "OF"; als: Voorwaarde[]; toon: string }

interface Keuze { id: string; naam: string }
interface Bouwstenen {
  vragen: { id: string; titel: string | null; perDier: boolean; opties: string[] }[];
  diersoorten: Keuze[];
  verblijven: Keuze[];
  extras: Keuze[];
}

const VASTE_VELDEN = ["diersoort", "verblijf", "extra", "verzekering"] as const;

function nieuweVoorwaarde(): Voorwaarde {
  return { veld: "diersoort", operator: "IS", waarde: "" };
}

export function WizardRegels({ t, vestigingId, regels, onChange }: {
  t: T;
  vestigingId: number | null;
  regels: Regel[];
  onChange: (regels: Regel[]) => void;
}) {
  const { data, isLoading } = useCustom<Bouwstenen>({
    url: "pension/wizard-indeling/versies/bouwstenen",
    method: "get",
    config: { query: vestigingId ? { vestigingId } : {} },
    queryOptions: { enabled: vestigingId != null },
  });
  const b = data?.data;

  if (vestigingId == null) {
    return <p className="kvd__stil">{t("portaalInstellingen.regels.eerstVestiging")}</p>;
  }
  if (isLoading || !b) {
    return <p className="kvd__stil">{t("common.loading")}</p>;
  }
  if (b.vragen.length === 0) {
    return <p className="kvd__stil">{t("portaalInstellingen.regels.geenVragen")}</p>;
  }

  const vraagTitel = (id: string) => b.vragen.find((v) => v.id === id)?.titel ?? id;
  const wijzigRegel = (i: number, nieuw: Partial<Regel>) => onChange(regels.map((r, j) => (j === i ? { ...r, ...nieuw } : r)));
  const wijzigVoorwaarde = (i: number, k: number, nieuw: Partial<Voorwaarde>) =>
    wijzigRegel(i, { als: regels[i].als.map((v, j) => (j === k ? { ...v, ...nieuw } : v)) });

  function waardeVeld(i: number, k: number, v: Voorwaarde) {
    const keuzes: Keuze[] | null =
      v.veld === "diersoort" ? b!.diersoorten
        : v.veld === "verblijf" ? b!.verblijven
          : v.veld === "extra" ? b!.extras
            : v.veld === "verzekering" ? [{ id: "ja", naam: t("portaalInstellingen.regels.ja") }, { id: "nee", naam: t("portaalInstellingen.regels.nee") }]
              : (b!.vragen.find((q) => `vraag:${q.id}` === v.veld)?.opties ?? []).map((o) => ({ id: o, naam: o }));
    const label = t("portaalInstellingen.regels.waarde");
    if (keuzes && keuzes.length > 0) {
      return (
        <select aria-label={label} required value={v.waarde} onChange={(e) => wijzigVoorwaarde(i, k, { waarde: e.target.value })}>
          <option value="">—</option>
          {keuzes.map((x) => <option key={x.id} value={x.id}>{x.naam}</option>)}
        </select>
      );
    }
    return <input aria-label={label} required maxLength={200} value={v.waarde} onChange={(e) => wijzigVoorwaarde(i, k, { waarde: e.target.value })} />;
  }

  return (
    <div className="pi__regels">
      {regels.length === 0 && <p className="kvd__stil">{t("portaalInstellingen.regels.leeg")}</p>}
      {regels.map((r, i) => (
        <fieldset key={r.id} className="pi__regel">
          <legend>{t("portaalInstellingen.regels.regel", { args: [i + 1] })}</legend>
          {r.als.map((v, k) => (
            <div key={k} className="pi__voorwaarde">
              <span className="pi__woord">
                {k === 0 ? t("portaalInstellingen.regels.als") : (
                  <select aria-label={t("portaalInstellingen.regels.verbinding")} value={r.verbinding}
                    onChange={(e) => wijzigRegel(i, { verbinding: e.target.value as "EN" | "OF" })}>
                    <option value="EN">{t("portaalInstellingen.regels.en")}</option>
                    <option value="OF">{t("portaalInstellingen.regels.of")}</option>
                  </select>
                )}
              </span>
              <select aria-label={t("portaalInstellingen.regels.veld")} value={v.veld}
                onChange={(e) => wijzigVoorwaarde(i, k, { veld: e.target.value, waarde: "" })}>
                {VASTE_VELDEN.map((x) => <option key={x} value={x}>{t(`portaalInstellingen.regels.veld.${x}`)}</option>)}
                {b.vragen.filter((q) => q.id !== r.toon).map((q) => (
                  <option key={q.id} value={`vraag:${q.id}`}>{t("portaalInstellingen.regels.antwoordOp", { args: [q.titel ?? q.id] })}</option>
                ))}
              </select>
              <select aria-label={t("portaalInstellingen.regels.operator")} value={v.operator}
                onChange={(e) => wijzigVoorwaarde(i, k, { operator: e.target.value as "IS" | "IS_NIET" })}>
                <option value="IS">{t("portaalInstellingen.regels.is")}</option>
                <option value="IS_NIET">{t("portaalInstellingen.regels.isNiet")}</option>
              </select>
              {waardeVeld(i, k, v)}
              {r.als.length > 1 && (
                <button type="button" className="kpt__link kpt__link--gevaar" aria-label={t("portaalInstellingen.regels.voorwaardeWeg")}
                  onClick={() => wijzigRegel(i, { als: r.als.filter((_, j) => j !== k) })}>
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
          {r.als.length < 10 && (
            <button type="button" className="kpt__link" onClick={() => wijzigRegel(i, { als: [...r.als, nieuweVoorwaarde()] })}>
              <Plus size={14} aria-hidden="true" /> {t("portaalInstellingen.regels.voorwaardeErbij")}
            </button>
          )}
          <div className="pi__voorwaarde">
            <span className="pi__woord">{t("portaalInstellingen.regels.dan")}</span>
            <select aria-label={t("portaalInstellingen.regels.toon")} required value={r.toon} onChange={(e) => wijzigRegel(i, { toon: e.target.value })}>
              <option value="">—</option>
              {b.vragen.map((q) => <option key={q.id} value={q.id}>{t("portaalInstellingen.regels.toonVraag", { args: [vraagTitel(q.id)] })}</option>)}
            </select>
            <button type="button" className="kpt__link kpt__link--gevaar" onClick={() => onChange(regels.filter((_, j) => j !== i))}>
              {t("portaalInstellingen.regels.regelWeg")}
            </button>
          </div>
        </fieldset>
      ))}
      {regels.length < 50 && (
        <div>
          <Button variant="secondary" type="button"
            onClick={() => onChange([...regels, { id: `regel-${Date.now().toString(36)}`, verbinding: "EN", als: [nieuweVoorwaarde()], toon: "" }])}>
            <Plus size={16} aria-hidden="true" /> {t("portaalInstellingen.regels.regelErbij")}
          </Button>
        </div>
      )}
    </div>
  );
}

interface Versie { versie: number; gepubliceerdOp: string; door: string | null; live: boolean }

/** De gepubliceerde wizardversies; terugzetten publiceert een oude versie opnieuw, alleen voor nieuwe aanvragen. */
export function WizardVersies({ t, vestigingId, formatDatum }: { t: T; vestigingId: number | null; formatDatum: (d: string) => string }) {
  const { data, refetch } = useCustom<Versie[]>({
    url: "pension/wizard-indeling/versies",
    method: "get",
    config: { query: vestigingId ? { vestigingId } : {} },
    queryOptions: { enabled: vestigingId != null },
  });
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const [melding, setMelding] = useState<{ tekst: string; fout: boolean } | null>(null);
  const versies = Array.isArray(data?.data) ? data.data : [];
  if (vestigingId == null) {
    return null;
  }

  async function terugzetten(versie: number) {
    if (!window.confirm(t("portaalInstellingen.versies.bevestig", { args: [versie] }))) {
      return;
    }
    setMelding(null);
    try {
      await mutateAsync({ url: `pension/wizard-indeling/versies/terugzetten?vestigingId=${vestigingId}`, method: "post",
        values: { versie }, successNotification: false, errorNotification: false });
      await refetch();
      setMelding({ tekst: t("portaalInstellingen.versies.teruggezet", { args: [versie] }), fout: false });
    } catch (err) {
      const code = (err as { statusCode?: number })?.statusCode;
      setMelding({ tekst: code === 403 ? t("portaalInstellingen.versies.geenRecht") : foutBody(err).melding ?? t("portaalInstellingen.fout"), fout: true });
    }
  }

  return (
    <div className="pi__versies">
      <h3>{t("portaalInstellingen.versies.titel")}</h3>
      <p className="kvd__stil">{t("portaalInstellingen.versies.uitleg")}</p>
      {melding && <p className={melding.fout ? "fawkes-error" : "pi__ok"} role={melding.fout ? "alert" : "status"}>{melding.tekst}</p>}
      {versies.length === 0 ? <p className="kvd__stil">{t("portaalInstellingen.versies.geen")}</p> : (
        <IndeelbareTabel
          tabelKey="portaal-wizard-versies"
          columns={[
            {
              key: "versie", header: t("portaalInstellingen.versies.versie"),
              cell: (v) => (
                <>{v.versie}{v.live && <> · <strong>{t("portaalInstellingen.versies.live")}</strong></>}</>
              ),
            },
            { key: "op", header: t("portaalInstellingen.versies.op"), cell: (v) => formatDatum(v.gepubliceerdOp) },
            { key: "door", header: t("portaalInstellingen.versies.door"), cell: (v) => v.door ?? "—" },
            {
              key: "acties", header: "", vast: true,
              cell: (v) => (!v.live ? (
                <button type="button" className="kpt__link" disabled={bezig} onClick={() => void terugzetten(v.versie)}>
                  <RotateCcw size={14} aria-hidden="true" /> {t("portaalInstellingen.versies.terugzetten")}
                </button>
              ) : null),
            },
          ]}
          rows={versies}
          getRowKey={(v) => String(v.versie)}
          empty=""
        />
      )}
    </div>
  );
}
